import { toString } from 'lodash';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS_KEYS,
} from 'pages/FundInvestments/common/constants';
import { generateColumnKey, getArrayValue, getObjectValue, getStringValue } from 'utilities';
import { CreateColumnsParams, FundInvestmentMarketAdjustmentColumn, MetricValues } from './createColumns.types';

const createColumns = (params: CreateColumnsParams): FundInvestmentMarketAdjustmentColumn[] => {
  const { comparisonsByDate, isSingleComparison } = params;

  // Ticker
  const ticker = getArrayValue(comparisonsByDate).reduce((accumulator, current) => {
    const { cap_iq_id: capIqId, ticker_symbol: tickerSymbol } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: getStringValue(capIqId),
      name: getStringValue(tickerSymbol),
      prefix: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: tickerSymbol,
      },
    };
  }, {}) as FundInvestmentMarketAdjustmentColumn;

  // Market cap
  const marketCap = getArrayValue(comparisonsByDate).reduce((accumulator, current) => {
    const { as_of_market_date: comparison, cap_iq_id: capIqId, ticker_symbol: tickerSymbol } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: getStringValue(capIqId),
      name: getStringValue(tickerSymbol),
      prefix: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: comparison?.market_cap,
      },
    };
  }, {}) as FundInvestmentMarketAdjustmentColumn;

  // Metric as of date
  const metricAsOfDate = getArrayValue(comparisonsByDate).reduce((accumulator, current) => {
    const { as_of_date: comparison, cap_iq_id: capIqId, ticker_symbol: tickerSymbol } = getObjectValue(current);
    const { is_index: isIndex, close_price: price } = getObjectValue(comparison);

    const columnKey = generateColumnKey({
      id: getStringValue(capIqId),
      name: getStringValue(tickerSymbol),
      prefix: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    const metricValues = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS_KEYS.reduce((metrics, key) => {
      const value = comparison[key as keyof typeof comparison];

      return {
        ...metrics,
        [key]: toString(value),
      };
    }, {} as MetricValues);

    return {
      ...accumulator,
      [columnKey]: {
        value: isIndex ? price : JSON.stringify(metricValues),
      },
    };
  }, {}) as FundInvestmentMarketAdjustmentColumn;

  // Metric as of market date
  const metricAsOfMarketDate = getArrayValue(comparisonsByDate).reduce((accumulator, current) => {
    const { as_of_market_date: comparison, cap_iq_id: capIqId, ticker_symbol: tickerSymbol } = getObjectValue(current);
    const { is_index: isIndex, close_price: price } = getObjectValue(comparison);

    const columnKey = generateColumnKey({
      id: getStringValue(capIqId),
      name: getStringValue(tickerSymbol),
      prefix: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    const metricValues = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS_KEYS.reduce((metrics, key) => {
      const value = comparison[key as keyof typeof comparison];

      return {
        ...metrics,
        [key]: toString(value),
      };
    }, {} as MetricValues);

    return {
      ...accumulator,
      [columnKey]: {
        value: isIndex ? price : JSON.stringify(metricValues),
      },
    };
  }, {}) as FundInvestmentMarketAdjustmentColumn;

  // Percentage change
  const percentageChange = getArrayValue(comparisonsByDate).reduce((accumulator, current) => {
    const { as_of_date: comparison, cap_iq_id: capIqId, ticker_symbol: tickerSymbol } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: getStringValue(capIqId),
      name: getStringValue(tickerSymbol),
      prefix: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: comparison.enabled,
      },
    };
  }, {}) as FundInvestmentMarketAdjustmentColumn;

  // Weighting
  const weighting = getArrayValue(comparisonsByDate).reduce((accumulator, current) => {
    const { as_of_date: comparison, cap_iq_id: capIqId, ticker_symbol: tickerSymbol } = getObjectValue(current);

    const columnKey = generateColumnKey({
      id: getStringValue(capIqId),
      name: getStringValue(tickerSymbol),
      prefix: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMN_KEY_PREFIX,
    });

    return {
      ...accumulator,
      [columnKey]: {
        value: comparison.weight,
      },
    };
  }, {}) as FundInvestmentMarketAdjustmentColumn;

  const columns = [ticker, marketCap, metricAsOfDate, metricAsOfMarketDate, percentageChange];

  // Add weighting column only for non-single comparison
  if (!isSingleComparison) {
    columns.push(weighting);
  }

  return columns;
};

export default createColumns;
